import { LevelDesignerComponent } from '../../level-designer/level-designer.component';
import { PropertyEditor } from '../property-editor';
import { PropertyInput } from './input-base';

export class TextInput extends PropertyInput {
    protected cursorPosition: number = 0;
    protected isSelected: boolean = false;
    protected textOffset: number = 0; // Tracks horizontal scroll position

    constructor(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        value: any,
        isActive: boolean,
        levelDesigner: LevelDesignerComponent,
        propertyEditor: PropertyEditor
    ) {
        super(ctx, x, y, width, height, value, isActive, levelDesigner, propertyEditor);
    }

    draw(): void {
        this.drawBackground();
        
        // Save context for clipping
        this.ctx.save();
        
        // Create clipping region for text
        this.ctx.beginPath();
        this.ctx.rect(this.x + 5, this.y + 2, this.width - 10, this.height - 4);
        this.ctx.clip();
        
        // Draw text
        this.ctx.fillStyle = '#000000';
        this.ctx.font = '12px Arial';
        this.ctx.textAlign = 'left';
        this.ctx.textBaseline = 'middle';
        
        if (this.isActive && this.isSelected) {
            // Draw selection
            const textWidth = this.ctx.measureText(this.displayValue).width;
            this.ctx.fillStyle = '#0078D7';
            this.ctx.fillRect(this.x + 5 - this.textOffset, this.y + 2, textWidth, this.height - 4);
            this.ctx.fillStyle = '#FFFFFF';
        }
        
        // Draw text with offset
        this.ctx.fillText(this.displayValue, this.x + 5 - this.textOffset, this.y + this.height / 2);

        // Draw cursor if active and not selected
        if (this.isActive && !this.isSelected) {
            this.drawCursor();
        }

        // Restore context (removes clipping)
        this.ctx.restore();
    }

    private drawCursor() {
        const cursorX = this.x + 5 + this.ctx.measureText(this.displayValue.slice(0, this.cursorPosition)).width - this.textOffset;
        
        // Only draw cursor if it's within the visible area
        if (cursorX >= this.x + 5 && cursorX <= this.x + this.width - 5) {
            this.ctx.beginPath();
            this.ctx.moveTo(cursorX, this.y + 4);
            this.ctx.lineTo(cursorX, this.y + this.height - 4);
            this.ctx.strokeStyle = '#000000';
            this.ctx.lineWidth = 1;
            this.ctx.stroke();
        }
    }

    private ensureCursorVisible() {
        if (!this.isActive) return;

        const cursorX = this.ctx.measureText(this.displayValue.slice(0, this.cursorPosition)).width;
        const visibleWidth = this.width - 10; // Account for padding

        // If cursor would be off-screen to the right
        if (cursorX - this.textOffset > visibleWidth) {
            this.textOffset = cursorX - visibleWidth + 5; // Add small padding
        }
        // If cursor would be off-screen to the left
        else if (cursorX - this.textOffset < 0) {
            this.textOffset = cursorX;
        }

        // Ensure we don't scroll past the start of the text
        this.textOffset = Math.max(0, this.textOffset);

        // Ensure we don't scroll past the end of the text
        const textWidth = this.ctx.measureText(this.displayValue).width;
        const maxOffset = Math.max(0, textWidth - visibleWidth);
        this.textOffset = Math.min(this.textOffset, maxOffset);
    }

    focus(): void {
        this.isActive = true;
        this.isSelected = true;  // Always select all text on focus
        this.cursorPosition = this.displayValue.length;
        this.ensureCursorVisible();
        if (this.levelDesigner) {
            this.levelDesigner.redraw();
        }
    }

    blur(): void {
        this.isActive = false;
        this.isSelected = false;
        this.textOffset = 0; // Reset scroll position when losing focus
    }

    handleClick(x: number, y: number): boolean {
        // Check if click is within input bounds
        const isInInputBounds = x >= this.x && x <= this.x + this.width && 
                               y >= this.y && y <= this.y + this.height;

        if (!isInInputBounds) {
            return false;
        }

        if (!this.isActive) {
            this.focus();
            return true;
        }

        // Calculate cursor position based on click location
        const clickX = x - (this.x + 5) + this.textOffset;
        let minDistance = Number.MAX_VALUE;
        let bestPosition = 0;

        // Find the character position closest to the click
        for (let i = 0; i <= this.displayValue.length; i++) {
            const charX = this.ctx.measureText(this.displayValue.slice(0, i)).width;
            const distance = Math.abs(charX - clickX);
            if (distance < minDistance) {
                minDistance = distance;
                bestPosition = i;
            }
        }

        this.cursorPosition = bestPosition;
        this.isSelected = false;
        this.ensureCursorVisible();
        return true;
    }

    protected isValidInputCharacter(key: string, currentValue: string, cursorPosition: number): boolean {
        return key.length === 1; // By default, allow any single character
    }

    public handleKeyEvent(event: KeyboardEvent): boolean {
        if (!this.isActive) return false;

        // Handle copy and paste operations
        if (event.ctrlKey || event.metaKey) {
            switch (event.key.toLowerCase()) {
                case 'c':
                    if (this.isSelected) {
                        event.preventDefault();
                        navigator.clipboard.writeText(this.displayValue);
                        return true;
                    }
                    break;
                case 'v':
                    event.preventDefault();
                    navigator.clipboard.readText().then(text => {
                        if (this.isSelected) {
                            this.displayValue = text;
                            this.cursorPosition = text.length;
                            this.isSelected = false;
                        } else {
                            this.displayValue = this.displayValue.slice(0, this.cursorPosition) + 
                                             text + 
                                             this.displayValue.slice(this.cursorPosition);
                            this.cursorPosition += text.length;
                        }
                        this.propertyEditor.commitValueFromInput(this, this.displayValue);
                        this.ensureCursorVisible();
                    });
                    return true;
            }
        }

        // Always prevent propagation for backspace and delete keys when input is active
        if (event.key === 'Backspace' || event.key === 'Delete') {
            event.stopPropagation();
            event.preventDefault();
        }

        let handled = false;

        // Ensure cursor position is within bounds
        this.cursorPosition = Math.min(Math.max(0, this.cursorPosition), this.displayValue.length);

        if (this.isSelected && this.isValidInputCharacter(event.key, this.displayValue, this.cursorPosition)) {
            this.displayValue = event.key;
            this.cursorPosition = 1;
            this.isSelected = false;
            handled = true;
        } else {
            switch (event.key) {
                case 'ArrowLeft':
                    if (this.isSelected) {
                        this.isSelected = false;
                        this.cursorPosition = 0;
                    } else if (this.cursorPosition > 0) {
                        this.cursorPosition--;
                    }
                    handled = true;
                    break;
                case 'ArrowRight':
                    if (this.isSelected) {
                        this.isSelected = false;
                        this.cursorPosition = this.displayValue.length;
                    } else if (this.cursorPosition < this.displayValue.length) {
                        this.cursorPosition++;
                    }
                    handled = true;
                    break;
                case 'Home':
                    event.preventDefault(); // Prevent browser scroll
                    this.cursorPosition = 0;
                    this.isSelected = false;
                    handled = true;
                    break;
                case 'End':
                    event.preventDefault(); // Prevent browser scroll
                    this.cursorPosition = this.displayValue.length;
                    this.isSelected = false;
                    handled = true;
                    break;
                case 'Backspace':
                    if (this.isSelected) {
                        this.displayValue = '';
                        this.cursorPosition = 0;
                        this.isSelected = false;
                        this.propertyEditor.commitValueFromInput(this, this.displayValue);
                    } else if (this.cursorPosition > 0) {
                        this.displayValue = this.displayValue.slice(0, this.cursorPosition - 1) + this.displayValue.slice(this.cursorPosition);
                        this.cursorPosition--;
                        this.propertyEditor.commitValueFromInput(this, this.displayValue);
                    }
                    handled = true;
                    break;
                case 'Delete':
                    if (this.isSelected) {
                        this.displayValue = '';
                        this.cursorPosition = 0;
                        this.isSelected = false;
                        this.propertyEditor.commitValueFromInput(this, this.displayValue);
                    } else if (this.cursorPosition < this.displayValue.length) {
                        this.displayValue = this.displayValue.slice(0, this.cursorPosition) + this.displayValue.slice(this.cursorPosition + 1);
                        this.propertyEditor.commitValueFromInput(this, this.displayValue);
                    }
                    handled = true;
                    break;
                default:
                    if (this.isValidInputCharacter(event.key, this.displayValue, this.cursorPosition)) {
                        this.displayValue = this.displayValue.slice(0, this.cursorPosition) + event.key + this.displayValue.slice(this.cursorPosition);
                        this.cursorPosition++;
                        handled = true;
                    }
            }
        }

        // Ensure cursor position is within bounds after any changes
        this.cursorPosition = Math.min(Math.max(0, this.cursorPosition), this.displayValue.length);

        if (handled) {
            this.ensureCursorVisible();
            if (this.propertyEditor) {
                this.propertyEditor.commitValueFromInput(this, this.displayValue);
            }
        }

        return handled;
    }

    override getValue(): any {
        return this.displayValue;
    }

    override setValue(value: any): void {
        super.setValue(value);
        this.displayValue = value?.toString() || '';
        this.ensureCursorVisible();
    }
} 
